import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Building2, Gamepad2, Loader2, Eye, EyeOff, Zap, GraduationCap } from 'lucide-react';
import axios from 'axios';
import { API, useAuth } from '../App';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const LoginPage = () => {
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState('solo'); // 'solo', 'school', or 'teacher'
  const [isSignup, setIsSignup] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Read URL parameters on mount
  useEffect(() => {
    const urlMode = searchParams.get('mode');
    const urlSignup = searchParams.get('signup');
    if (urlMode && ['solo', 'school', 'teacher'].includes(urlMode)) {
      setMode(urlMode);
    }
    if (urlSignup === 'true') {
      setIsSignup(true);
    }
  }, [searchParams]);
  
  // Solo mode form - support multiple children
  const [soloForm, setSoloForm] = useState({ email: '', password: '' });
  const [children, setChildren] = useState([{ name: '', age: 6, startingLevel: 1 }]);
  
  const addChild = () => {
    setChildren([...children, { name: '', age: 6, startingLevel: 1 }]);
  };
  
  const removeChild = (index) => {
    if (children.length > 1) {
      setChildren(children.filter((_, i) => i !== index));
    }
  };
  
  const updateChild = (index, field, value) => {
    const updated = [...children];
    updated[index][field] = value;
    if (field === 'age') {
      updated[index].startingLevel = getRecommendedLevel(value);
    }
    setChildren(updated);
  };
  
  // Age to recommended level mapping
  const getRecommendedLevel = (age) => {
    if (age <= 5) return 1;
    if (age <= 6) return 2;
    if (age <= 8) return 3;
    if (age <= 10) return 4;
    return 5;
  };
  
  const levelNames = {
    1: { name: 'Newcomer', icon: '🏕️', desc: 'Simple single words' },
    2: { name: 'Explorer', icon: '🗺️', desc: 'Two or three words' },
    3: { name: 'Builder', icon: '🏗️', desc: 'Short phrases' },
    4: { name: 'Master', icon: '⭐', desc: 'Full sentences' },
    5: { name: 'Champion', icon: '🏆', desc: 'Paragraphs' },
  };
  
  // School mode form
  const [schoolForm, setSchoolForm] = useState({ schoolCode: '', username: '', password: '' });
  
  // Teacher mode form
  const [teacherForm, setTeacherForm] = useState({ email: '', password: '', name: '', schoolName: '' });
  
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleSoloSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      let response;
      if (isSignup) {
        // Send first child for initial signup, additional children added after
        response = await axios.post(`${API}/auth/parent/signup`, {
          email: soloForm.email,
          password: soloForm.password,
          child_name: children[0].name,
          child_age: parseInt(children[0].age) || 6,
          starting_level: parseInt(children[0].startingLevel) || 1,
          additional_children: children.slice(1).map(c => ({
            name: c.name,
            age: parseInt(c.age) || 6,
            starting_level: parseInt(c.startingLevel) || 1
          }))
        });
      } else {
        response = await axios.post(`${API}/auth/parent/login`, {
          email: soloForm.email,
          password: soloForm.password
        });
      }
      
      const { token } = response.data;
      const userResponse = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      await login(token, userResponse.data);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSchoolSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.post(`${API}/auth/school/login`, {
        school_code: schoolForm.schoolCode,
        username: schoolForm.username,
        password: schoolForm.password
      });
      
      const { token } = response.data;
      const userResponse = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      await login(token, userResponse.data);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid school code or credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleTeacherSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      let response;
      if (isSignup) {
        response = await axios.post(`${API}/auth/teacher/signup`, {
          email: teacherForm.email,
          password: teacherForm.password,
          name: teacherForm.name,
          school_name: teacherForm.schoolName
        });
      } else {
        response = await axios.post(`${API}/auth/teacher/login`, {
          email: teacherForm.email,
          password: teacherForm.password
        });
      }
      
      const { token } = response.data;
      const userResponse = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      await login(token, userResponse.data);
      navigate('/teacher');
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-100 via-white to-orange-50">
      <Navbar />
      
      <section className="pt-32 pb-20 px-4">
        <div className="max-w-md mx-auto">
          {/* Logo */}
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-2">
              <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center border-2 border-gray-200 shadow-md">
                <span className="text-2xl">🏠</span>
              </div>
              <span className="font-display text-3xl font-bold">
                <span className="text-mint-green">Type</span>
                <span className="text-coral-orange">Town</span>
              </span>
            </Link>
          </div>
          
          {/* Mode Toggle - 3 options */}
          <div className="flex bg-gray-100 rounded-2xl p-1.5 mb-8 shadow-inner" data-testid="login-mode-toggle">
            <button
              onClick={() => { setMode('solo'); setError(''); setIsSignup(false); }}
              className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-sm transition-all ${
                mode === 'solo' ? 'bg-sunny-yellow text-text-dark shadow-md' : 'text-text-medium hover:text-text-dark'
              }`}
              data-testid="login-solo-tab"
            >
              <Gamepad2 className="w-4 h-4" />
              Home
            </button>
            <button
              onClick={() => { setMode('school'); setError(''); setIsSignup(false); }}
              className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-sm transition-all ${
                mode === 'school' ? 'bg-mint-green text-white shadow-md' : 'text-text-medium hover:text-text-dark'
              }`}
              data-testid="login-school-tab"
            >
              <Building2 className="w-4 h-4" />
              Pupil
            </button>
            <button
              onClick={() => { setMode('teacher'); setError(''); setIsSignup(false); }}
              className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-sm transition-all ${
                mode === 'teacher' ? 'bg-lavender text-white shadow-md' : 'text-text-medium hover:text-text-dark'
              }`}
              data-testid="login-teacher-tab"
            >
              <GraduationCap className="w-4 h-4" />
              Teacher
            </button>
          </div>
          
          {/* Error Message */}
          {error && (
            <div className="bg-coral-orange/10 border-2 border-coral-orange/30 rounded-2xl p-4 mb-6" data-testid="login-error">
              <p className="font-bold text-sm text-coral-orange">{error}</p>
            </div>
          )}
          
          {/* Solo Mode Form */}
          {mode === 'solo' && (
            <div className="bg-white rounded-3xl p-6 shadow-lg border-2 border-gray-100">
              <h2 className="font-display text-2xl font-bold text-text-dark text-center mb-6">
                {isSignup ? 'Join TypeTown!' : 'Welcome Back!'}
              </h2>
              {isSignup && (
                <p className="text-center text-sm text-mint-green font-semibold -mt-4 mb-6">100% free · No card required</p>
              )}
              <form onSubmit={handleSoloSubmit} className="space-y-4" data-testid="solo-login-form">
                {isSignup && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <label className="block font-bold text-sm text-text-dark">Children</label>
                      <button
                        type="button"
                        onClick={addChild}
                        className="text-sm font-bold text-mint-green hover:text-mint-green/80 flex items-center gap-1"
                        data-testid="add-child-btn"
                      >
                        + Add Another Child
                      </button>
                    </div>
                    
                    {children.map((child, index) => (
                      <div key={index} className="bg-gray-50 rounded-2xl p-4 space-y-3 relative">
                        {children.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeChild(index)}
                            className="absolute top-2 right-2 text-coral-orange hover:text-coral-orange/80 text-sm font-bold"
                            data-testid={`remove-child-${index}`}
                          >
                            ✕
                          </button>
                        )}
                        
                        <div className="text-xs font-bold text-text-medium mb-2">
                          Child {index + 1}
                        </div>
                        
                        <input
                          type="text"
                          required
                          value={child.name}
                          onChange={(e) => updateChild(index, 'name', e.target.value)}
                          className="w-full bg-white border-2 border-gray-200 text-text-dark font-body rounded-xl p-2.5 focus:border-mint-green outline-none transition-all placeholder:text-text-medium/50 text-sm"
                          placeholder="Child's name"
                          data-testid={`child-name-${index}`}
                        />
                        
                        <div className="flex gap-2">
                          <select
                            value={child.age}
                            onChange={(e) => updateChild(index, 'age', parseInt(e.target.value))}
                            className="flex-1 bg-white border-2 border-gray-200 text-text-dark font-body rounded-xl p-2.5 focus:border-mint-green outline-none transition-all text-sm"
                            data-testid={`child-age-${index}`}
                          >
                            {[4,5,6,7,8,9,10,11,12].map(age => (
                              <option key={age} value={age}>{age} years</option>
                            ))}
                          </select>
                          
                          <select
                            value={child.startingLevel}
                            onChange={(e) => updateChild(index, 'startingLevel', parseInt(e.target.value))}
                            className="flex-1 bg-white border-2 border-gray-200 text-text-dark font-body rounded-xl p-2.5 focus:border-mint-green outline-none transition-all text-sm"
                            data-testid={`child-level-${index}`}
                          >
                            {[1,2,3,4,5].map(level => (
                              <option key={level} value={level}>
                                {levelNames[level].icon} Level {level}
                                {level === getRecommendedLevel(child.age) ? ' ★' : ''}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    ))}
                    
                    <p className="text-xs text-text-medium">
                      💡 Level is auto-set based on age. You can add more children later from your dashboard.
                    </p>
                  </div>
                )}
                
                <div>
                  <label className="block font-bold text-sm text-text-dark mb-2">Parent Email</label>
                  <input
                    type="email"
                    required
                    value={soloForm.email}
                    onChange={(e) => setSoloForm({...soloForm, email: e.target.value})}
                    className="w-full bg-gray-50 border-2 border-gray-200 text-text-dark font-body rounded-2xl p-3 focus:border-mint-green outline-none transition-all placeholder:text-text-medium/50"
                    placeholder="parent@email.com"
                    data-testid="solo-email"
                  />
                </div>
                
                <div>
                  <label className="block font-bold text-sm text-text-dark mb-2">Password</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      required
                      minLength={6}
                      value={soloForm.password}
                      onChange={(e) => setSoloForm({...soloForm, password: e.target.value})}
                      className="w-full bg-gray-50 border-2 border-gray-200 text-text-dark font-body rounded-2xl p-3 pr-12 focus:border-mint-green outline-none transition-all placeholder:text-text-medium/50"
                      placeholder="••••••••"
                      data-testid="solo-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-screen-white/50 hover:text-screen-white"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full font-display uppercase tracking-wider bg-zap-yellow text-deep-space px-6 py-4 rounded-sm hover:translate-y-1 hover:shadow-none shadow-[0_4px_0_0_#B2A000] transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="solo-submit"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : null}
                  {isSignup ? 'Create Account' : 'Log In'}
                </button>
              </form>
              
              <div className="mt-6 text-center space-y-2">
                <Link
                  to="/forgot-password"
                  className="font-body text-sm text-screen-white/60 hover:text-bot-cyan hover:underline block"
                  data-testid="solo-forgot-password"
                >
                  Forgot your password?
                </Link>
                <button
                  onClick={() => { setIsSignup(!isSignup); setError(''); }}
                  className="font-body text-sm text-bot-cyan hover:underline"
                  data-testid="solo-toggle-signup"
                >
                  {isSignup ? 'Already have an account? Log in' : 'Need an account? Sign up'}
                </button>
              </div>
            </div>
          )}
          
          {/* School Mode Form */}
          {mode === 'school' && (
            <div className="bg-dark-panel border-2 border-mid-panel rounded-xl p-6">
              <h2 className="font-display text-2xl text-screen-white text-center mb-6">Pupil Login</h2>
              
              <form onSubmit={handleSchoolSubmit} className="space-y-4" data-testid="school-login-form">
                <div>
                  <label className="block font-body font-semibold text-sm text-screen-white mb-2">School Code</label>
                  <input
                    type="text"
                    required
                    value={schoolForm.schoolCode}
                    onChange={(e) => setSchoolForm({...schoolForm, schoolCode: e.target.value.toUpperCase()})}
                    className="w-full bg-deep-space border-2 border-mid-panel text-screen-white font-mono text-center text-xl tracking-[0.3em] rounded-lg p-3 focus:border-bot-cyan outline-none transition-all placeholder:text-screen-white/30 uppercase"
                    placeholder="ABC123"
                    maxLength={6}
                    data-testid="school-code"
                  />
                  <p className="font-mono text-xs text-screen-white/40 mt-1">Ask your teacher for the code</p>
                </div>
                
                <div>
                  <label className="block font-body font-semibold text-sm text-screen-white mb-2">Username</label>
                  <input
                    type="text"
                    required
                    value={schoolForm.username}
                    onChange={(e) => setSchoolForm({...schoolForm, username: e.target.value})}
                    className="w-full bg-deep-space border-2 border-mid-panel text-screen-white font-body rounded-lg p-3 focus:border-bot-cyan outline-none transition-all placeholder:text-screen-white/30"
                    placeholder="Your username"
                    data-testid="school-username"
                  />
                </div>
                
                <div>
                  <label className="block font-body font-semibold text-sm text-screen-white mb-2">Password</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      required
                      value={schoolForm.password}
                      onChange={(e) => setSchoolForm({...schoolForm, password: e.target.value})}
                      className="w-full bg-deep-space border-2 border-mid-panel text-screen-white font-body rounded-lg p-3 pr-12 focus:border-bot-cyan outline-none transition-all placeholder:text-screen-white/30"
                      placeholder="••••••••"
                      data-testid="school-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-screen-white/50 hover:text-screen-white"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full font-display uppercase tracking-wider bg-bot-cyan text-deep-space px-6 py-4 rounded-sm hover:translate-y-1 hover:shadow-none shadow-[0_4px_0_0_#008B73] transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="school-submit"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : null}
                  Enter The Grid
                </button>
              </form>
            </div>
          )}
          
          {/* Teacher Mode Form */}
          {mode === 'teacher' && (
            <div className="bg-dark-panel border-2 border-glitch-red/30 rounded-xl p-6">
              <h2 className="font-display text-2xl text-screen-white text-center mb-6">
                {isSignup ? 'Teacher Sign Up' : 'Teacher Login'}
              </h2>
              {isSignup && (
                <p className="text-center text-sm text-bot-cyan font-semibold -mt-4 mb-6">100% free · No card required</p>
              )}
              <form onSubmit={handleTeacherSubmit} className="space-y-4" data-testid="teacher-login-form">
                {isSignup && (
                  <>
                    <div>
                      <label className="block font-body font-semibold text-sm text-screen-white mb-2">Your Name</label>
                      <input
                        type="text"
                        required
                        value={teacherForm.name}
                        onChange={(e) => setTeacherForm({...teacherForm, name: e.target.value})}
                        className="w-full bg-deep-space border-2 border-mid-panel text-screen-white font-body rounded-lg p-3 focus:border-glitch-red outline-none transition-all placeholder:text-screen-white/30"
                        placeholder="Mrs. Smith"
                        data-testid="teacher-name"
                      />
                    </div>
                    <div>
                      <label className="block font-body font-semibold text-sm text-screen-white mb-2">School / Class Name</label>
                      <input
                        type="text"
                        value={teacherForm.schoolName}
                        onChange={(e) => setTeacherForm({...teacherForm, schoolName: e.target.value})}
                        className="w-full bg-deep-space border-2 border-mid-panel text-screen-white font-body rounded-lg p-3 focus:border-glitch-red outline-none transition-all placeholder:text-screen-white/30"
                        placeholder="e.g. Oakwood Primary (optional)"
                        data-testid="teacher-school-name"
                      />
                      <p className="mt-2 text-xs text-screen-white/50">We'll create your class and give you a School Code to share with your pupils.</p>
                    </div>
                  </>
                )}
                
                <div>
                  <label className="block font-body font-semibold text-sm text-screen-white mb-2">Email</label>
                  <input
                    type="email"
                    required
                    value={teacherForm.email}
                    onChange={(e) => setTeacherForm({...teacherForm, email: e.target.value})}
                    className="w-full bg-deep-space border-2 border-mid-panel text-screen-white font-body rounded-lg p-3 focus:border-glitch-red outline-none transition-all placeholder:text-screen-white/30"
                    placeholder="teacher@school.edu"
                    data-testid="teacher-email"
                  />
                </div>
                
                <div>
                  <label className="block font-body font-semibold text-sm text-screen-white mb-2">Password</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      required
                      minLength={6}
                      value={teacherForm.password}
                      onChange={(e) => setTeacherForm({...teacherForm, password: e.target.value})}
                      className="w-full bg-deep-space border-2 border-mid-panel text-screen-white font-body rounded-lg p-3 pr-12 focus:border-glitch-red outline-none transition-all placeholder:text-screen-white/30"
                      placeholder="••••••••"
                      data-testid="teacher-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-screen-white/50 hover:text-screen-white"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full font-display uppercase tracking-wider bg-glitch-red text-white px-6 py-4 rounded-sm hover:translate-y-1 hover:shadow-none shadow-[0_4px_0_0_#991B2E] transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="teacher-submit"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : null}
                  {isSignup ? 'Create Teacher Account' : 'Teacher Login'}
                </button>
              </form>
              
              <div className="mt-6 text-center space-y-2">
                <Link
                  to="/forgot-password"
                  className="font-body text-sm text-screen-white/60 hover:text-glitch-red hover:underline block"
                  data-testid="teacher-forgot-password"
                >
                  Forgot your password?
                </Link>
                <button
                  onClick={() => { setIsSignup(!isSignup); setError(''); }}
                  className="font-body text-sm text-glitch-red hover:underline"
                  data-testid="teacher-toggle-signup"
                >
                  {isSignup ? 'Already have an account? Log in' : 'Need a teacher account? Sign up'}
                </button>
              </div>
            </div>
          )}
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default LoginPage;
